import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  LocalGasStation,
  ArrowForward,
  TrendingUp,
  AttachMoney
} from '@mui/icons-material';
import FuelLogList from './FuelLogList';
import FuelLogForm from './FuelLogForm';
import { useNavigate } from 'react-router-dom';

const FuelLogsPage = () => {
  const [open, setOpen] = useState(false);
  const [editFuelLog, setEditFuelLog] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();

  const handleAdd = () => {
    setEditFuelLog(null);
    setIsEditing(true);
    setOpen(true);
  };

  const handleEdit = (fuelLog) => {
    setEditFuelLog(fuelLog);
    setIsEditing(false);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditFuelLog(null);
    setIsEditing(false);
  };

  const handleFormClose = (shouldRefetch) => {
    handleClose();
    if (shouldRefetch) {
      // Trigger a refetch of the data instead of full page reload
      // The parent component should handle the refetch
    }
  };

  return (
    <Box sx={{ 
      p: 4, 
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', 
      minHeight: '100vh' 
    }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: '#10b981',
              width: 56,
              height: 56,
              mr: 2,
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
            }}
          >
            <LocalGasStation sx={{ fontSize: 28 }} />
          </Avatar>
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 0.5
              }}
            >
              Fuel Logs
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400 }}>
              Track fuel consumption and costs across your fleet
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Action Cards */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid #e2e8f0',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(16, 185, 129, 0.2)',
              }
            }} onClick={handleAdd}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Avatar
                  sx={{
                    bgcolor: '#10b981',
                    width: 64,
                    height: 64,
                    mx: 'auto',
                    mb: 2,
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
                  }}
                >
                  <AddIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Add Fuel Log
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Record a new fuel purchase or consumption in gallons
            </Typography>
                <Chip
                  label="Quick Add"
                  color="success"
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid #e2e8f0',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(99, 102, 241, 0.2)',
              }
            }} onClick={() => navigate('/fleet/vehicles')}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Avatar
                  sx={{
                    bgcolor: '#6366f1',
                    width: 64,
                    height: 64,
                    mx: 'auto',
                    mb: 2,
                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)'
                  }}
                >
                  <ArrowForward sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Vehicle Fleet
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  View vehicle fuel efficiency and consumption
                </Typography>
                <Chip
                  label="View All"
                  color="primary"
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              </CardContent>
            </Card>
          </Grid>
          {/* <Grid item xs={12} md={4}>
            <Card sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid #e2e8f0',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(6, 182, 212, 0.2)',
              }
            }} onClick={() => navigate('/fleet/trips')}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Avatar
                  sx={{
                    bgcolor: '#06b6d4',
                    width: 64,
                    height: 64,
                    mx: 'auto',
                    mb: 2,
                    boxShadow: '0 4px 12px rgba(6, 182, 212, 0.4)'
                  }}
                >
                  <TrendingUp sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Trip Analytics
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  Analyze fuel consumption per trip
                </Typography>
                <Chip
                  label="Analyze"
                  color="info"
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              </CardContent>
            </Card>
          </Grid> */}
        </Grid>
      </Box>

      {/* Fuel Log List */}
      <Card sx={{
        borderRadius: 3,
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #e2e8f0'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
            Fuel Consumption History
          </Typography>
      <FuelLogList onEdit={handleEdit} />
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: 'white',
          fontWeight: 600
        }}>
          {editFuelLog ? (isEditing ? 'Edit Fuel Log' : 'View Fuel Log') : 'Add Fuel Log'}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <FuelLogForm 
            logId={editFuelLog?.id}
            onClose={handleFormClose}
            isEditing={isEditing}
            onToggleEdit={() => setIsEditing(!isEditing)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FuelLogsPage; 